/**
 * Local-only persistence for Jain Gaurav Yatra (no Supabase).
 * State is stored in localStorage under jgy_local_db_v1.
 */
import type { CertSettings } from "@/lib/certificate";

const STORAGE_KEY = "jgy_local_db_v1";
const LEGACY_BOOKINGS_KEY = "demo_bookings";

export const DEMO_USER_ID = "00000000-0000-0000-0000-000000000001";

function nowIso() {
  return new Date().toISOString();
}

function uid() {
  return crypto.randomUUID();
}

export type TempleRow = {
  id: string;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  yatra_order: number;
  mool_nayak: string;
  established_year: number | null;
  latitude: number | null;
  longitude: number | null;
  history_en: string;
  history_hi: string;
  history_gu: string;
  qr_code: string;
  image_url: string | null;
  created_at: string;
};

export type ProfileRow = {
  id: string;
  full_name: string | null;
  phone: string | null;
  area: string | null;
  language: string;
  created_at: string;
};

export type VisitRow = { id: string; user_id: string; temple_id: string; visited_at: string };

export type AnnouncementRow = {
  id: string;
  title_en: string;
  title_hi: string;
  title_gu: string;
  body_en: string;
  body_hi: string;
  body_gu: string;
  active: boolean;
  image_url: string | null;
  created_at: string;
};

export type VolunteerRow = {
  id: string;
  name: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string | null;
  bio_hi: string | null;
  bio_gu: string | null;
  active: boolean;
  created_at: string;
};

export type RickshawRow = {
  id: string;
  driver_name: string;
  phone: string;
  vehicle_number: string;
  area: string | null;
  charges_per_trip: number;
  active: boolean;
  created_at: string;
};

export type BookingRow = {
  id: string;
  user_id: string;
  type: "volunteer" | "rickshaw" | "visit";
  booking_date: string;
  booking_time: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  volunteer_id: string | null;
  rickshaw_id: string | null;
  notes: string | null;
  group_size: number | null;
  visitor_names: string | null;
  confirmed_at: string | null;
  created_at: string;
};

export type CertificateRow = {
  id: string;
  user_id: string;
  certificate_number: string;
  issued_at: string;
  visited_count: number;
  total_temples: number;
  issued_by: string | null;
};

export type UserRoleRow = { id: string; user_id: string; role: "admin" | "member"; created_at: string };

export type CertificateSettingsRow = CertSettings & { id: number; updated_at: string };

type DbState = {
  temples: TempleRow[];
  profiles: ProfileRow[];
  temple_visits: VisitRow[];
  announcements: AnnouncementRow[];
  volunteers: VolunteerRow[];
  rickshaws: RickshawRow[];
  bookings: BookingRow[];
  certificates: CertificateRow[];
  certificate_settings: CertificateSettingsRow;
  user_roles: UserRoleRow[];
};

const AREAS = [
  "Navrangpura", "Paldi", "Ambawadi", "Ellisbridge", "Shahibaug", "Relief Road", "Kalupur", "Sarangpur",
  "Maninagar", "Vastrapur", "Bodakdev", "Satellite", "Gurukul", "Memnagar", "Naranpura", "Ranip",
  "Chandkheda", "Motera", "Sabarmati", "Ashram Road", "Law Garden", "C.G. Road", "Odhav", "Naroda",
  "Bapunagar", "Khokhra", "Isanpur", "Vatva", "Amraiwadi", "Ramol", "Nikol", "Gota",
];

function seedTemples(): TempleRow[] {
  const baseLat = 23.03;
  const baseLng = 72.58;
  const out: TempleRow[] = [];
  for (let i = 1; i <= 31; i++) {
    const order = i;
    const area = AREAS[(i - 1) % AREAS.length];
    const angle = (i / 31) * Math.PI * 2;
    const lat = baseLat + Math.sin(angle) * 0.04 + (i % 5) * 0.002;
    const lng = baseLng + Math.cos(angle) * 0.045 + (i % 3) * 0.0015;
    const id = `jgy-temple-${String(i).padStart(2, "0")}`;
    out.push({
      id,
      name_en: `Jain Derasar ${order} — ${area}`,
      name_hi: `जैन देरासर ${order} — ${area}`,
      name_gu: `જૈન દેરાસર ${order} — ${area}`,
      area,
      yatra_order: order,
      mool_nayak: order % 3 === 0 ? "Bhagwan Parshvanath" : order % 2 === 0 ? "Bhagwan Adinath" : "Bhagwan Mahavir",
      established_year: 1800 + (i * 7) % 200,
      latitude: Math.round(lat * 1e6) / 1e6,
      longitude: Math.round(lng * 1e6) / 1e6,
      history_en: `A sacred derasar on the Jain Gaurav Yatra circuit in ${area}, Ahmedabad. Timings may vary on festival days — please confirm at the temple.`,
      history_hi: `अहमदाबाद के ${area} में जैन गौरव यात्रा मार्ग पर यह पावन देरासर है।`,
      history_gu: `અમદાવાદના ${area} વિસ્તારમાં જૈન ગૌરવ યાત્રા માર્ગ પર આ પવિત્ર દેરાસર છે.`,
      qr_code: `JGY-T${String(i).padStart(2, "0")}`,
      image_url: null,
      created_at: nowIso(),
    });
  }
  return out;
}

function seedVolunteers(): VolunteerRow[] {
  const t = nowIso();
  return [
    { id: uid(), name: "Kiran Shah", phone: "+91 98765 43210", languages: ["English", "Gujarati"], experience_years: 8, charges_per_day: 800, bio_en: "Heritage walks & derasar stories.", bio_hi: null, bio_gu: null, active: true, created_at: t },
    { id: uid(), name: "Meera Desai", phone: "+91 98250 11223", languages: ["Hindi", "Gujarati"], experience_years: 5, charges_per_day: 650, bio_en: "Family-friendly yatra planning.", bio_hi: null, bio_gu: null, active: true, created_at: t },
  ];
}

function seedRickshaws(): RickshawRow[] {
  const t = nowIso();
  return [
    { id: uid(), driver_name: "Ramesh Bhai", phone: "+91 99099 00111", vehicle_number: "GJ01XX1234", area: "Navrangpura", charges_per_trip: 120, active: true, created_at: t },
    { id: uid(), driver_name: "Hasmukh Bhai", phone: "+91 99099 00222", vehicle_number: "GJ01YY5678", area: "Paldi", charges_per_trip: 150, active: true, created_at: t },
  ];
}

function seedAnnouncements(): AnnouncementRow[] {
  const t = nowIso();
  return [
    {
      id: uid(),
      title_en: "Welcome to Jain Gaurav Yatra 2026",
      title_hi: "जैन गौरव यात्रा 2026 में आपका स्वागत",
      title_gu: "જૈન ગૌરવ યાત્રા ૨૦૨૬માં આપનું સ્વાગત",
      body_en: "Scan QR codes at each derasar, track your progress, and book seva support from the app.",
      body_hi: "प्रत्येक देरासर पर QR स्कैन करें और ऐप से अपनी यात्रा ट्रैक करें।",
      body_gu: "દરેક દેરાસરે QR સ્કેન કરો અને એપથી યાત્રા ટ્રૅક કરો.",
      active: true,
      image_url: null,
      created_at: t,
    },
  ];
}

function defaultCertSettings(): CertificateSettingsRow {
  const t = nowIso();
  return {
    id: 1,
    updated_at: t,
    title_en: "Certificate of Yatra Completion",
    title_hi: "यात्रा पूर्णता प्रमाणपत्र",
    title_gu: "યાત્રા પૂર્ણતા પ્રમાણપત્ર",
    message_en: "This certifies devoted participation in the Jain Gaurav Yatra across Ahmedabad's derasars.",
    message_hi: "यह प्रमाणित करता है कि यात्री ने अहमदाबाद की यात्रा में भाग लिया।",
    message_gu: "આ પ્રમાણિત કરે છે કે યાત્રી અમદાવાદની યાત્રામાં ભાગ લીધો.",
    signatory_name: "Yatra Committee",
    signatory_title_en: "Organising Secretary",
    signatory_title_hi: "आयोजन सचिव",
    signatory_title_gu: "આયોજન સચિવ",
  };
}

function freshDb(): DbState {
  return {
    temples: seedTemples(),
    profiles: [
      {
        id: DEMO_USER_ID,
        full_name: "Demo Devotee",
        phone: null,
        area: null,
        language: "en",
        created_at: nowIso(),
      },
    ],
    temple_visits: [],
    announcements: seedAnnouncements(),
    volunteers: seedVolunteers(),
    rickshaws: seedRickshaws(),
    bookings: [],
    certificates: [],
    certificate_settings: defaultCertSettings(),
    user_roles: [],
  };
}

function readDb(): DbState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const db = freshDb();
      writeDb(db);
      migrateLegacyBookings(db);
      return db;
    }
    const parsed = JSON.parse(raw) as Partial<DbState>;
    const base = freshDb();
    const merged: DbState = {
      temples: parsed.temples?.length ? (parsed.temples as TempleRow[]) : base.temples,
      profiles: parsed.profiles?.length ? (parsed.profiles as ProfileRow[]) : base.profiles,
      temple_visits: Array.isArray(parsed.temple_visits) ? (parsed.temple_visits as VisitRow[]) : [],
      announcements: parsed.announcements?.length ? (parsed.announcements as AnnouncementRow[]) : base.announcements,
      volunteers: parsed.volunteers?.length ? (parsed.volunteers as VolunteerRow[]) : base.volunteers,
      rickshaws: parsed.rickshaws?.length ? (parsed.rickshaws as RickshawRow[]) : base.rickshaws,
      bookings: Array.isArray(parsed.bookings) ? (parsed.bookings as BookingRow[]) : [],
      certificates: Array.isArray(parsed.certificates) ? (parsed.certificates as CertificateRow[]) : [],
      certificate_settings: parsed.certificate_settings?.title_en
        ? (parsed.certificate_settings as CertificateSettingsRow)
        : base.certificate_settings,
      user_roles: Array.isArray(parsed.user_roles) ? (parsed.user_roles as UserRoleRow[]) : [],
    };
    migrateLegacyBookings(merged);
    return merged;
  } catch {
    const db = freshDb();
    writeDb(db);
    return db;
  }
}

function migrateLegacyBookings(db: DbState) {
  try {
    const leg = localStorage.getItem(LEGACY_BOOKINGS_KEY);
    if (!leg) {return;}
    const arr = JSON.parse(leg) as Partial<BookingRow>[];
    if (!Array.isArray(arr) || arr.length === 0) {
      localStorage.removeItem(LEGACY_BOOKINGS_KEY);
      return;
    }
    for (const b of arr) {
      if (!b?.id) {continue;}
      db.bookings.push({
        id: b.id,
        user_id: b.user_id || DEMO_USER_ID,
        type: (b.type as BookingRow["type"]) || "visit",
        booking_date: b.booking_date || nowIso().slice(0, 10),
        booking_time: b.booking_time || "08:00",
        status: (b.status as BookingRow["status"]) || "confirmed",
        volunteer_id: b.volunteer_id ?? null,
        rickshaw_id: b.rickshaw_id ?? null,
        notes: b.notes ?? null,
        group_size: b.group_size ?? 1,
        visitor_names: b.visitor_names ?? null,
        confirmed_at: b.status === "confirmed" ? nowIso() : null,
        created_at: b.created_at || nowIso(),
      });
    }
    localStorage.removeItem(LEGACY_BOOKINGS_KEY);
    writeDb(db);
  } catch {
    /* ignore */
  }
}

function writeDb(db: DbState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
}

function sortTemples(list: TempleRow[]) {
  return [...list].sort((a, b) => a.yatra_order - b.yatra_order);
}

// ——— Public API ———

export function listTemplesFull() {
  return sortTemples(readDb().temples);
}

export function listTemplesSummary() {
  return sortTemples(readDb().temples).map((t) => ({
    id: t.id,
    name_en: t.name_en,
    name_hi: t.name_hi,
    name_gu: t.name_gu,
    area: t.area,
    yatra_order: t.yatra_order,
    mool_nayak: t.mool_nayak,
  }));
}

export function listTemplesForMap() {
  return sortTemples(readDb().temples).map((t) => ({
    id: t.id,
    name_en: t.name_en,
    name_hi: t.name_hi,
    name_gu: t.name_gu,
    area: t.area,
    yatra_order: t.yatra_order,
    latitude: t.latitude,
    longitude: t.longitude,
  }));
}

export function getTempleById(id: string): TempleRow | null {
  return readDb().temples.find((t) => t.id === id) || null;
}

export function getTempleByQrCode(qr: string): { id: string; name_en: string } | null {
  const t = readDb().temples.find((x) => x.qr_code.toUpperCase() === qr.toUpperCase());
  return t ? { id: t.id, name_en: t.name_en } : null;
}

export function countTemples() {
  return readDb().temples.length;
}

export function listVisitTempleIds(userId: string) {
  return readDb().temple_visits.filter((v) => v.user_id === userId).map((v) => v.temple_id);
}

export function hasVisit(userId: string, templeId: string) {
  return readDb().temple_visits.some((v) => v.user_id === userId && v.temple_id === templeId);
}

export function insertVisit(userId: string, templeId: string): { ok: true } | { ok: false; message: string } {
  const db = readDb();
  if (db.temple_visits.some((v) => v.user_id === userId && v.temple_id === templeId)) {
    return { ok: false, message: "Already checked in" };
  }
  db.temple_visits.push({ id: uid(), user_id: userId, temple_id: templeId, visited_at: nowIso() });
  writeDb(db);
  return { ok: true };
}

export function getProfile(userId: string): ProfileRow | null {
  return readDb().profiles.find((p) => p.id === userId) || null;
}

export function upsertProfile(userId: string, patch: { full_name?: string; area?: string | null; phone?: string | null }) {
  const db = readDb();
  let p = db.profiles.find((x) => x.id === userId);
  if (!p) {
    p = { id: userId, full_name: null, phone: null, area: null, language: "en", created_at: nowIso() };
    db.profiles.push(p);
  }
  if (patch.full_name !== undefined) {p.full_name = patch.full_name;}
  if (patch.area !== undefined) {p.area = patch.area;}
  if (patch.phone !== undefined) {p.phone = patch.phone;}
  writeDb(db);
}

export function listActiveAnnouncementTitles(limit?: number) {
  let list = readDb().announcements.filter((a) => a.active).sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (limit) {list = list.slice(0, limit);}
  return list.map((a) => ({ id: a.id, title_en: a.title_en, title_hi: a.title_hi, title_gu: a.title_gu }));
}

export function listAnnouncementsAll() {
  return [...readDb().announcements].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function insertAnnouncement(row: Omit<AnnouncementRow, "id" | "created_at"> & { id?: string }) {
  const db = readDb();
  const a: AnnouncementRow = {
    id: row.id || uid(),
    title_en: row.title_en,
    title_hi: row.title_hi,
    title_gu: row.title_gu,
    body_en: row.body_en,
    body_hi: row.body_hi,
    body_gu: row.body_gu,
    active: row.active,
    image_url: row.image_url ?? null,
    created_at: nowIso(),
  };
  db.announcements.unshift(a);
  writeDb(db);
}

export function toggleAnnouncementActive(id: string) {
  const db = readDb();
  const a = db.announcements.find((x) => x.id === id);
  if (a) {
    a.active = !a.active;
    writeDb(db);
  }
}

export function deleteAnnouncement(id: string) {
  const db = readDb();
  db.announcements = db.announcements.filter((x) => x.id !== id);
  writeDb(db);
}

export function listVolunteers(activeOnly?: boolean) {
  let v = [...readDb().volunteers].sort((a, b) => a.name.localeCompare(b.name));
  if (activeOnly) {v = v.filter((x) => x.active);}
  return v;
}

export function saveVolunteer(row: Partial<VolunteerRow> & { name: string; phone: string }) {
  const db = readDb();
  const t = nowIso();
  if (row.id) {
    const i = db.volunteers.findIndex((v) => v.id === row.id);
    if (i >= 0) {db.volunteers[i] = { ...db.volunteers[i], ...row } as VolunteerRow;}
  } else {
    db.volunteers.push({
      id: uid(),
      name: row.name,
      phone: row.phone,
      languages: row.languages || ["English"],
      experience_years: row.experience_years ?? 0,
      charges_per_day: row.charges_per_day ?? 500,
      bio_en: row.bio_en ?? null,
      bio_hi: row.bio_hi ?? null,
      bio_gu: row.bio_gu ?? null,
      active: row.active !== false,
      created_at: t,
    });
  }
  writeDb(db);
}

export function deleteVolunteer(id: string) {
  const db = readDb();
  db.volunteers = db.volunteers.filter((v) => v.id !== id);
  writeDb(db);
}

export function listRickshaws(activeOnly?: boolean) {
  let r = [...readDb().rickshaws].sort((a, b) => a.driver_name.localeCompare(b.driver_name));
  if (activeOnly) {r = r.filter((x) => x.active);}
  return r;
}

export function saveRickshaw(row: Partial<RickshawRow> & { driver_name: string; phone: string; vehicle_number: string }) {
  const db = readDb();
  const t = nowIso();
  if (row.id) {
    const i = db.rickshaws.findIndex((r) => r.id === row.id);
    if (i >= 0) {db.rickshaws[i] = { ...db.rickshaws[i], ...row } as RickshawRow;}
  } else {
    db.rickshaws.push({
      id: uid(),
      driver_name: row.driver_name,
      phone: row.phone,
      vehicle_number: row.vehicle_number,
      area: row.area ?? null,
      charges_per_trip: row.charges_per_trip ?? 150,
      active: row.active !== false,
      created_at: t,
    });
  }
  writeDb(db);
}

export function deleteRickshaw(id: string) {
  const db = readDb();
  db.rickshaws = db.rickshaws.filter((r) => r.id !== id);
  writeDb(db);
}

export function listTempleVisits(): VisitRow[] {
  return [...readDb().temple_visits];
}

export function deleteBookingForUser(id: string, userId: string) {
  const db = readDb();
  db.bookings = db.bookings.filter((b) => !(b.id === id && b.user_id === userId));
  writeDb(db);
}

export function listBookings() {
  return [...readDb().bookings].sort((a, b) => b.booking_date.localeCompare(a.booking_date));
}

export function appendBooking(
  b: Omit<BookingRow, "id" | "created_at" | "confirmed_at"> & { id?: string; confirmed_at?: string | null; created_at?: string },
) {
  const db = readDb();
  const row: BookingRow = {
    id: b.id || uid(),
    user_id: b.user_id,
    type: b.type,
    booking_date: b.booking_date,
    booking_time: b.booking_time,
    status: b.status,
    volunteer_id: b.volunteer_id,
    rickshaw_id: b.rickshaw_id,
    notes: b.notes,
    group_size: b.group_size,
    visitor_names: b.visitor_names,
    confirmed_at: b.confirmed_at ?? (b.status === "confirmed" ? nowIso() : null),
    created_at: b.created_at || nowIso(),
  };
  db.bookings.unshift(row);
  writeDb(db);
}

export function updateBooking(id: string, patch: Partial<Pick<BookingRow, "status" | "confirmed_at">>) {
  const db = readDb();
  const row = db.bookings.find((x) => x.id === id);
  if (!row) {return;}
  if (patch.status !== undefined) {row.status = patch.status;}
  if (patch.confirmed_at !== undefined) {row.confirmed_at = patch.confirmed_at;}
  else if (patch.status === "confirmed" && !row.confirmed_at) {row.confirmed_at = nowIso();}
  writeDb(db);
}

export function listMyBookings(userId: string) {
  return listBookings().filter((b) => b.user_id === userId);
}

/**
 * Bookings mapped to the admin list shape (member/provider names resolved).
 * Lets the admin Bookings tab surface visits submitted from the local app.
 */
export function listBookingsForAdmin() {
  const db = readDb();
  const profileName = (userId: string) =>
    db.profiles.find((p) => p.id === userId)?.full_name || "Devotee";
  const profilePhone = (userId: string) => db.profiles.find((p) => p.id === userId)?.phone || "";
  const providerName = (b: BookingRow) => {
    if (b.volunteer_id) {
      return db.volunteers.find((v) => v.id === b.volunteer_id)?.name || "Volunteer";
    }
    if (b.rickshaw_id) {
      return db.rickshaws.find((r) => r.id === b.rickshaw_id)?.driver_name || "Rickshaw";
    }
    return b.type === "visit" ? "Temple Visit" : "";
  };
  return listBookings().map((b) => ({
    id: b.id,
    type: b.type,
    member_name: profileName(b.user_id),
    member_phone: profilePhone(b.user_id),
    member_email: "",
    provider_name: providerName(b),
    // Locally-submitted bookings have no walk/guide association.
    walk_name: "",
    guide_name: "",
    guide_phone: "",
    guide_email: "",
    volunteer_name: "",
    volunteer_phone: "",
    volunteer_email: "",
    volunteer_gender: "",
    volunteer_status: "",
    volunteer_accepted_at: null,
    is_volunteer_walk: null,
    walk_price: 0,
    walk_is_free: null,
    meet_up: "",
    starting_point: "",
    booking_date: b.booking_date,
    booking_time: b.booking_time,
    status: b.status,
    notes: b.notes,
    group_size: b.group_size,
    visitor_names: b.visitor_names,
    confirmed_at: b.confirmed_at,
    created_at: b.created_at,
    updated_at: b.created_at,
    is_free_cancellation: null,
    cancellation_charge_now: 0,
    // Locally-submitted bookings carry none of the admin-only assignment/payment fields.
    walk_id: "",
    walk_is_guide: null,
    walk_is_breakfast: null,
    walk_is_volunteer: null,
    time_slot_display: "",
    status_display: "",
    meeting_point: "",
    adults: 0,
    children: 0,
    seniors: 0,
    preferred_volunteer_gender: "any",
    preferred_guide_gender: "any",
    guide_id: "",
    volunteer_id: b.volunteer_id ? String(b.volunteer_id) : "",
    rickshaw_id: b.rickshaw_id ? String(b.rickshaw_id) : "",
    breakfast_id: "",
    amount_paid: 0,
    final_amount: 0,
    payment_status: "",
    payment_method: "",
    cancelled_at: null,
    cancellation_charge: 0,
    refund_amount: 0,
  }));
}

export function listCertificates() {
  return [...readDb().certificates].sort((a, b) => b.issued_at.localeCompare(a.issued_at));
}

export function listCertificatesForUser(userId: string) {
  return listCertificates().filter((c) => c.user_id === userId);
}

export function getLatestCertificateForUser(userId: string) {
  const list = listCertificatesForUser(userId);
  return list[0] || null;
}

export function insertCertificate(row: {
  user_id: string;
  visited_count: number;
  total_temples: number;
  issued_by?: string | null;
}) {
  const db = readDb();
  const num = `JGY-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900000) + 100000)}`;
  const c: CertificateRow = {
    id: uid(),
    user_id: row.user_id,
    certificate_number: num,
    issued_at: nowIso(),
    visited_count: row.visited_count,
    total_temples: row.total_temples,
    issued_by: row.issued_by ?? null,
  };
  db.certificates.unshift(c);
  writeDb(db);
  return c;
}

export function getCertificateSettings() {
  return readDb().certificate_settings;
}

export function saveCertificateSettings(s: CertificateSettingsRow) {
  const db = readDb();
  db.certificate_settings = { ...s, id: 1, updated_at: nowIso() };
  writeDb(db);
}

export function listProfilesOrdered() {
  return [...readDb().profiles].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function countActiveAnnouncements() {
  return readDb().announcements.filter((a) => a.active).length;
}

export function countAdmins() {
  return readDb().user_roles.filter((r) => r.role === "admin").length;
}

export function listAdminUserIds() {
  return readDb().user_roles.filter((r) => r.role === "admin").map((r) => r.user_id);
}

export function addAdminRole(userId: string) {
  const db = readDb();
  if (db.user_roles.some((r) => r.user_id === userId && r.role === "admin")) {return;}
  db.user_roles.push({ id: uid(), user_id: userId, role: "admin", created_at: nowIso() });
  writeDb(db);
}

export function removeAdminRole(userId: string) {
  const db = readDb();
  db.user_roles = db.user_roles.filter((r) => !(r.user_id === userId && r.role === "admin"));
  writeDb(db);
}

export function saveTemple(row: TempleRow) {
  const db = readDb();
  const i = db.temples.findIndex((t) => t.id === row.id);
  if (i >= 0) {db.temples[i] = { ...row };}
  else {db.temples.push({ ...row, created_at: row.created_at || nowIso() });}
  writeDb(db);
}

export function insertTemple(row: Omit<TempleRow, "created_at"> & Partial<Pick<TempleRow, "created_at">>) {
  const db = readDb();
  const t: TempleRow = {
    ...(row as TempleRow),
    id: row.id || uid(),
    created_at: row.created_at || nowIso(),
  };
  db.temples.push(t);
  writeDb(db);
}

export function deleteTemple(id: string) {
  const db = readDb();
  db.temples = db.temples.filter((t) => t.id !== id);
  db.temple_visits = db.temple_visits.filter((v) => v.temple_id !== id);
  writeDb(db);
}

export function getDashboardSnapshot() {
  const db = readDb();
  const temples = sortTemples(db.temples);
  return {
    counts: {
      temples: db.temples.length,
      members: db.profiles.length,
      ann: db.announcements.filter((a) => a.active).length,
      bookings: db.bookings.length,
      visits: db.temple_visits.length,
      volunteers: db.volunteers.length,
      rickshaws: db.rickshaws.length,
      certs: db.certificates.length,
    },
    recentTemples: temples.slice(0, 6).map((t) => ({
      id: t.id,
      name_en: t.name_en,
      area: t.area,
      yatra_order: t.yatra_order,
      mool_nayak: t.mool_nayak,
    })),
    recentAnnouncements: [...db.announcements]
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, 4)
      .map((a) => ({ id: a.id, title_en: a.title_en, body_en: a.body_en, active: a.active, created_at: a.created_at })),
    templesByArea: (() => {
      const m = new Map<string, number>();
      db.temples.forEach((t) => m.set(t.area, (m.get(t.area) || 0) + 1));
      return Array.from(m.entries())
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);
    })(),
    bookingsStatusType: (() => {
      const sMap = new Map<string, number>();
      const tMap = new Map<string, number>();
      db.bookings.forEach((b) => {
        sMap.set(b.status, (sMap.get(b.status) || 0) + 1);
        tMap.set(b.type, (tMap.get(b.type) || 0) + 1);
      });
      return {
        byStatus: Array.from(sMap.entries()).map(([name, value]) => ({ name, value })),
        byType: Array.from(tMap.entries()).map(([name, value]) => ({ name, value })),
      };
    })(),
    topVolunteers: [...db.volunteers]
      .sort((a, b) => b.experience_years - a.experience_years)
      .slice(0, 6)
      .map((v) => ({ name: v.name, experience_years: v.experience_years, charges_per_day: v.charges_per_day })),
  };
}
